// Omnichannel WP3 dilim 3 — gelen kutusunda konuşma yönetimi paneli: saf HTML (DOM ve ağ yok → birim test edilebilir).
// Denetleyici: js/views/gelen-kutusu.ts · Uçlar: /api/v2/omni/conversations/:id/{yonetim,durum,atama,etiketler,notlar}
// Test: tests/omnichannel/konusma-yonetimi-view.test.js
//
// Notlar, etiketler ve atanan adı personelden gelen metindir: her dinamik metin escHtml, her öznitelik escAttr(escHtml()).
// İç notlar YALNIZ ekip içindir; müşteriye gitmez, müşteri panelinde görünmez.

import { escHtml, escAttr } from '../utils.js';

const h = (s: unknown): string => escHtml(s ?? '');
const attr = (s: unknown): string => escAttr(escHtml(s ?? ''));

export interface IcNot { id: string; govde: string; yazar: string; zaman: string }

/** WP3 dilim 4 — markanın hazır cevabı. `dolu`: {ad}/{marka} bu konuşmanın değerleriyle doldurulmuş metin. */
export interface HazirCevap { id: string; baslik: string; metin: string; dolu: string; guncelleyen?: string; guncelleme?: string }

/** Liste satırındaki SLA özeti (sunucu: notify/omnichannel/core/sla.ts). */
export interface SlaOzeti {
  durum: 'kapali' | 'yanitlandi' | 'ilk_yanit_bekliyor' | 'yanit_bekliyor' | 'mesaj_yok';
  gecikti: boolean;
  bekleyenDk: number;
  pencereKalanDk: number | null;
  pencereKapaniyor: boolean;
}

export interface KonusmaYonetimi {
  durum: string;
  atama: null | { tur: 'staff' | 'agent'; kimlik: string; zaman: string | null; atayan: string | null };
  etiketler: string[];
  notlar: IcNot[];
  hermesAjani: string | null;
  etiketOnerileri: string[];
  hazirCevaplar?: HazirCevap[];
}

export const DURUM_ETIKETLERI: Record<string, string> = {
  open: 'Açık', waiting_customer: 'Müşteri yanıtı bekleniyor', waiting_agent: 'Ekip yanıtı bekleniyor',
  closed: 'Kapandı', archived: 'Arşiv',
};

const zaman = (z: string | null | undefined): string => (z ? String(z).replace('T', ' ').slice(0, 16) : '—');

/** "staff:yilmaz" → "yilmaz"; ajan kimliği olduğu gibi. */
export function atananAdi(a: KonusmaYonetimi['atama']): string {
  if (!a) return 'atanmadı';
  return a.tur === 'staff' ? a.kimlik.replace(/^staff:/, '') : `${a.kimlik} (Hermes)`;
}

/** Liste satırı için kısa rozet metni; atama yoksa boş. */
export function atamaRozeti(a: null | { tur: 'staff' | 'agent'; kimlik: string }): string {
  if (!a) return '';
  return a.tur === 'staff' ? `👤 ${a.kimlik.replace(/^staff:/, '')}` : `🤖 ${a.kimlik}`;
}

/** "95 dk" / "3 sa 20 dk" / "2 gün 4 sa". */
export function sureYaz(dk: number): string {
  const d = Math.max(0, Math.floor(dk));
  if (d < 60) return `${d} dk`;
  if (d < 24 * 60) return `${Math.floor(d / 60)} sa${d % 60 ? ` ${d % 60} dk` : ''}`;
  const gun = Math.floor(d / (24 * 60));
  const sa = Math.floor((d % (24 * 60)) / 60);
  return `${gun} gün${sa ? ` ${sa} sa` : ''}`;
}

/** Liste satırı SLA rozetleri: [metin, renk]. Yanıtlanmış/kapalı konuşmada rozet yok. */
export function slaRozetleri(s: SlaOzeti | undefined | null): Array<[string, string]> {
  if (!s || (s.durum !== 'ilk_yanit_bekliyor' && s.durum !== 'yanit_bekliyor')) return [];
  const out: Array<[string, string]> = [];
  if (s.gecikti) out.push([`SLA aşıldı · ${sureYaz(s.bekleyenDk)}`, '#b83232']);
  else out.push([`yanıt bekliyor · ${sureYaz(s.bekleyenDk)}`, '#8a6d1f']);
  if (s.pencereKapaniyor && s.pencereKalanDk !== null) out.push([`pencere kapanıyor · ${sureYaz(s.pencereKalanDk)}`, '#b85c00']);
  return out;
}

/** Yanıt kutusunun üstündeki hazır cevap seçimi. Liste boşsa hiçbir şey çizilmez. */
export function hazirCevapSecimiHtml(liste: HazirCevap[] | undefined, kapali = false): string {
  if (!liste || liste.length === 0) return '';
  const secenekler = liste.map((c) => `<option value="${attr(c.id)}">${h(c.baslik)}</option>`).join('');
  return `<div class="gk-hazir-secim" style="display:flex;gap:6px;align-items:center;margin-top:4px;">
      <label for="gk-hazir-sec" style="font-size:11px;color:var(--muted);">Hazır cevap</label>
      <select id="gk-hazir-sec" class="form-select" style="padding:3px 8px;max-width:260px;" ${kapali ? 'disabled' : ''}><option value="">— seçin —</option>${secenekler}</select>
      <button type="button" class="btn-secondary" data-action="gk-hazir-kullan" ${kapali ? 'disabled' : ''} style="padding:3px 10px;font-size:12px;">Yanıta ekle</button>
    </div>`;
}

/** Seçilen hazır cevabı yanıt metnine ekler: boşsa yerine koyar, doluysa bir satır boşlukla sona ekler. */
export function hazirCevabiEkle(mevcut: string, eklenecek: string): string {
  return mevcut.trim() ? `${mevcut.replace(/\s+$/, '')}\n\n${eklenecek}` : eklenecek;
}

function hazirCevapYonetimiHtml(liste: HazirCevap[]): string {
  const satirlar = liste.map((c) => `<details class="gk-hazir" style="border-top:1px solid var(--border,#2a2a2a);padding:4px 0;">
      <summary style="cursor:pointer;font-size:12px;">${h(c.baslik)}</summary>
      <input id="gk-hazir-baslik-${attr(c.id)}" class="form-input" value="${attr(c.baslik)}" maxlength="80" style="width:100%;box-sizing:border-box;margin-top:4px;padding:3px 8px;" />
      <textarea id="gk-hazir-metin-${attr(c.id)}" class="form-input" rows="3" maxlength="1000" style="width:100%;box-sizing:border-box;margin-top:4px;">${h(c.metin)}</textarea>
      <div style="display:flex;gap:6px;justify-content:flex-end;margin-top:4px;">
        <button type="button" class="btn-secondary" data-action="gk-hazir-guncelle" data-hazir-id="${attr(c.id)}" style="padding:3px 10px;font-size:12px;">Kaydet</button>
        <button type="button" class="btn-secondary" data-action="gk-hazir-arsivle" data-hazir-id="${attr(c.id)}" style="padding:3px 10px;font-size:12px;">Arşivle</button>
      </div>
    </details>`).join('');
  return `<details class="gk-hazir-yonetim" style="margin-top:10px;">
      <summary style="cursor:pointer;font-size:11px;color:var(--muted);">Hazır cevaplar — bu markanın (${liste.length}) · yer tutucular: {ad}, {marka}</summary>
      ${satirlar || '<div style="font-size:12px;color:var(--muted);margin:4px 0;">Bu markanın hazır cevabı yok.</div>'}
      <div style="border-top:1px solid var(--border,#2a2a2a);padding-top:6px;margin-top:4px;">
        <input id="gk-hazir-baslik" class="form-input" maxlength="80" placeholder="Başlık, ör. Fiyat bilgisi" style="width:100%;box-sizing:border-box;padding:3px 8px;" />
        <textarea id="gk-hazir-metin" class="form-input" rows="3" maxlength="1000" placeholder="Merhaba {ad}, …" style="width:100%;box-sizing:border-box;margin-top:4px;"></textarea>
        <div style="display:flex;justify-content:flex-end;margin-top:4px;">
          <button type="button" class="btn-secondary" data-action="gk-hazir-ekle" style="padding:3px 10px;font-size:12px;">Hazır cevap ekle</button>
        </div>
      </div>
    </details>`;
}

/** Etiket kutusu metnini diziye çevirir (virgülle ayrılmış). Normalleştirme sunucuda. */
export function etiketMetniniAyir(metin: string): string[] {
  return metin.split(',').map((x) => x.trim()).filter(Boolean);
}

/** `null` = yüklenemedi → panel kısa bir uyarı çizer (konuşma akışı yine görünür). */
export function yonetimPaneliHtml(y: KonusmaYonetimi | null): string {
  if (!y) {
    return '<div class="gk-yonetim" style="font-size:12px;color:var(--muted);margin:6px 0;">Konuşma yönetimi bilgisi yüklenemedi.</div>';
  }
  const durumSecenekleri = Object.entries(DURUM_ETIKETLERI)
    .map(([k, v]) => `<option value="${attr(k)}"${k === y.durum ? ' selected' : ''}>${h(v)}</option>`)
    .join('');
  const hermesDugmesi = y.hermesAjani
    ? `<button type="button" class="btn-secondary" data-action="gk-ata" data-hedef="hermes" style="padding:3px 10px;font-size:12px;">Hermes'e ata</button>`
    : `<button type="button" class="btn-secondary" disabled title="Bu markanın aktif Hermes ajanı yok" style="padding:3px 10px;font-size:12px;">Hermes'e ata</button>`;
  const kaldirDugmesi = y.atama
    ? `<button type="button" class="btn-secondary" data-action="gk-ata" data-hedef="kaldir" style="padding:3px 10px;font-size:12px;">Atamayı kaldır</button>`
    : '';
  const atamaAyrinti = y.atama
    ? `<span style="color:var(--muted);"> · ${h(zaman(y.atama.zaman))}${y.atama.atayan ? ` · atayan ${h(String(y.atama.atayan).replace(/^staff:/, ''))}` : ''}</span>`
    : '';
  const oneriler = y.etiketOnerileri.map((e) => `<option value="${attr(e)}"></option>`).join('');
  const notlar = y.notlar.length
    ? y.notlar.map((n) => `<div class="gk-not" style="padding:6px 8px;border-radius:6px;background:var(--bg2);margin-bottom:6px;">
        <div style="font-size:10px;color:var(--muted);margin-bottom:2px;">${h(String(n.yazar).replace(/^staff:/, ''))} · ${h(zaman(n.zaman))}</div>
        <div style="font-size:12px;white-space:pre-wrap;word-break:break-word;">${h(n.govde)}</div></div>`).join('')
    : '<div style="font-size:12px;color:var(--muted);">Henüz iç not yok.</div>';

  return `<details class="gk-yonetim" open style="margin:6px 0 10px;border:1px solid var(--border,#2a2a2a);border-radius:8px;padding:8px 10px;">
    <summary style="cursor:pointer;font-size:12px;font-weight:600;">Konuşma yönetimi</summary>
    <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center;margin-top:8px;">
      <label for="gk-durum-secim" style="font-size:11px;color:var(--muted);">Durum</label>
      <select id="gk-durum-secim" class="form-select" style="padding:3px 8px;">${durumSecenekleri}</select>
      <button type="button" class="btn-secondary" data-action="gk-durum" style="padding:3px 10px;font-size:12px;">Durumu kaydet</button>
    </div>
    <div style="display:flex;gap:6px;flex-wrap:wrap;align-items:center;margin-top:8px;font-size:12px;">
      <span>Atanan: <strong class="gk-atanan">${h(atananAdi(y.atama))}</strong>${atamaAyrinti}</span>
      <button type="button" class="btn-secondary" data-action="gk-ata" data-hedef="ben" style="padding:3px 10px;font-size:12px;">Bana ata</button>
      ${hermesDugmesi}${kaldirDugmesi}
    </div>
    <div style="display:flex;gap:6px;flex-wrap:wrap;align-items:center;margin-top:8px;">
      <label for="gk-etiketler" style="font-size:11px;color:var(--muted);">Etiketler</label>
      <input id="gk-etiketler" class="form-input" list="gk-etiket-onerileri" value="${attr(y.etiketler.join(', '))}"
        placeholder="virgülle ayırın, ör. teklif, destek" maxlength="440" style="flex:1;min-width:180px;padding:3px 8px;" />
      <datalist id="gk-etiket-onerileri">${oneriler}</datalist>
      <button type="button" class="btn-secondary" data-action="gk-etiket-kaydet" style="padding:3px 10px;font-size:12px;">Etiketleri kaydet</button>
    </div>
    <div style="margin-top:10px;">
      <div style="font-size:11px;color:var(--muted);margin-bottom:4px;">İç notlar — yalnız ekip görür, müşteriye gitmez</div>
      <div class="gk-notlar" style="max-height:24vh;overflow-y:auto;">${notlar}</div>
      <textarea id="gk-not-metin" class="form-input" rows="2" maxlength="4000" placeholder="Ekip için not…" style="width:100%;box-sizing:border-box;margin-top:4px;"></textarea>
      <div style="display:flex;justify-content:flex-end;margin-top:4px;">
        <button type="button" class="btn-secondary" data-action="gk-not-ekle" style="padding:3px 10px;font-size:12px;">Not ekle</button>
      </div>
    </div>
    ${hazirCevapYonetimiHtml(y.hazirCevaplar ?? [])}
  </details>`;
}
